import { Component, ReactNode } from 'react'
import moment from 'moment'

type BadgeVariant = 'info' | 'warning'

export function MatchCardShell({ children }: { children: ReactNode }) {
  return <div className="bg-white border border-gray-200 rounded-2xl p-4 mb-3">{children}</div>
}

export function MatchStubBadge({ label, variant }: { label: string; variant: BadgeVariant }) {
  const styles =
    variant === 'info'
      ? 'bg-primary-50 text-primary-700'
      : 'bg-warn bg-opacity-20 text-warn'
  return (
    <span className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full ${styles}`}>
      {label}
    </span>
  )
}

export function MatchStubHeader({ children }: { children: ReactNode }) {
  return <div className="flex items-center justify-between gap-2">{children}</div>
}

export function MatchStubEventName({ name }: { name?: string }) {
  if (!name) return null
  return <p className="mt-2 text-sm font-semibold text-navy-900">{name}</p>
}

export function MatchStubFooter({ matchDate }: { matchDate?: string }) {
  if (!matchDate) return null
  return (
    <div className="flex justify-end mt-3">
      <span className="text-xs text-navy-900 text-opacity-50">
        {moment(matchDate).format('MMMM D, YYYY')}
      </span>
    </div>
  )
}

export function MatchStubDeltaPill({ delta }: { delta: number }) {
  if (delta === 0) return null
  const isUp = delta > 0
  const sign = isUp ? '+' : ''
  return (
    <span className={`text-xs font-semibold ${isUp ? 'text-green' : 'text-warn'}`}>
      {sign}
      {delta.toFixed(3)} {isUp ? '▲' : '▼'}
    </span>
  )
}

export function MatchRecordedFallback({ matchDate }: { matchDate?: string }) {
  return (
    <MatchCardShell>
      <p className="text-sm text-navy-900">Match recorded</p>
      <MatchStubFooter matchDate={matchDate} />
    </MatchCardShell>
  )
}

type BoundaryProps = { children: ReactNode; matchDate?: string }
type BoundaryState = { hasError: boolean }

export class MatchStubErrorBoundary extends Component<BoundaryProps, BoundaryState> {
  state: BoundaryState = { hasError: false }

  static getDerivedStateFromError(): BoundaryState {
    return { hasError: true }
  }

  componentDidCatch(error: Error) {
    if (import.meta.env.DEV) {
      console.error('Match stub render error:', error)
    }
  }

  render() {
    if (this.state.hasError) {
      return <MatchRecordedFallback matchDate={this.props.matchDate} />
    }
    return this.props.children
  }
}
